import React from 'react';
import { 
  AppBar, 
  Toolbar, 
  IconButton,
  useTheme,
  Container,
  Box,
  Tooltip,
  useMediaQuery,
  Button
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import LogoutIcon from '@mui/icons-material/Logout';
import SupportIcon from '@mui/icons-material/SupportAgent';
import { t } from '../utils/translations';

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, isAuthenticated } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActiveRoute = (path: string) => location.pathname === path;

  return (
    <AppBar 
      position="static" 
      elevation={10}
      sx={{ 
        backgroundColor: 'white', 
        borderBottom: '1px solid', 
        borderColor: 'divider',
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)'
      }}
    >
      <Container maxWidth="lg">
        <Toolbar 
          disableGutters 
          sx={{ 
            height: { xs: '70px', sm: '80px' }, 
            justifyContent: 'space-between',
            px: { xs: 1, sm: 2 }
          }}
        >
          <Box>
            <img 
              src="/logo.png"
              alt="Bambino Logo" 
              style={{ 
                height: '50px', 
                cursor: 'pointer'
              }}
              onClick={() => navigate(isAuthenticated ? '/requests' : '/login')}
            />
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              color="primary"
              variant={isActiveRoute('/support') ? "contained" : "text"}
              startIcon={<SupportIcon />}
              onClick={() => navigate('/support')}
              sx={{ 
                display: { xs: 'none', sm: 'flex' },
                borderRadius: 2,
                ...(isActiveRoute('/support') && {
                  background: 'linear-gradient(45deg, #ec248f 30%, #ff6eb5 90%)',
                  boxShadow: '0 4px 10px rgba(236, 36, 143, 0.25)',
                })
              }}
            >
              {t('Support')}
            </Button>
            
            {/* Mobile Support Icon */}
            <Tooltip title={t('Support')}>
              <IconButton
                onClick={() => navigate('/support')}
                sx={{ 
                  display: { xs: 'flex', sm: 'none' },
                  width: 36, 
                  height: 36,
                  bgcolor: isActiveRoute('/support') ? '#ec248f' : theme.palette.grey[100],
                  color: isActiveRoute('/support') ? 'white' : '#ec248f',
                  '&:hover': {
                    bgcolor: isActiveRoute('/support') ? '#d41c7f' : theme.palette.grey[200],
                  }
                }}
              >
                <SupportIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            
            {isAuthenticated && (
              <Tooltip title={t('Logout')}>
                <IconButton
                  onClick={handleLogout}
                  sx={{ 
                    width: { xs: 36, sm: 40 }, 
                    height: { xs: 36, sm: 40 },
                    bgcolor: theme.palette.grey[100],
                    color: '#ec248f',
                    '&:hover': {
                      bgcolor: theme.palette.grey[200],
                    }
                  }}
                >
                  <LogoutIcon fontSize={isMobile ? "small" : "medium"} />
                </IconButton>
              </Tooltip>
            )}
            
            {!isAuthenticated && location.pathname === '/support' && (
              <Button
                variant="outlined"
                color="primary"
                onClick={() => navigate('/login')}
                sx={{ 
                  borderRadius: 2,
                  borderColor: 'rgba(236, 36, 143, 0.5)',
                  color: '#ec248f',
                  '&:hover': {
                    backgroundColor: 'rgba(236, 36, 143, 0.04)',
                    borderColor: '#ec248f'
                  }
                }}
              >
                {t('Login')}
              </Button>
            )}
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default Header; 